/**
 * O painel ao lado do exame na mesa única (E201.F2, GUS-47, ADR-0144 §8; E202.F1, GUS-145,
 * ADR-0145 §5), com duas abas:
 * - "Documentos": o documento original (`DocumentosDoPainel`), que "Ver na fonte" abre no arquivo e
 *   na página citados;
 * - "Prévia (PDF)": a prévia definitiva, o PDF que a preparação guardou da saída mostrada no centro.
 *   Enquanto a preparação refaz a saída, o painel diz que a prévia está sendo atualizada e não mostra
 *   o arquivo antigo (`PreviaDaSaida`).
 */
import type { AxiosInstance } from 'axios';

import { Button, Seg } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { SaidaDaEdicao } from '@/lib/correcaoDoRascunho';
import type { LinhaDoPacoteData } from '@/lib/preparacao_do_pacote';

import type { DocumentoAberto } from './ControlesDoDocumento';
import { DocumentosDoPainel } from './DocumentosDoPainel';
import { PreviaDaSaida } from './PreviaDaSaida';
import type { AbaDoPainel } from './usePainelDaMesa';

const t = ptBR.mesaUnica.documento;
const tp = ptBR.documentoDaMesa.painel;

export type { DocumentoAberto };

/** A prévia da saída mostrada no centro: a linha dela no pacote e se o pacote está preparando. */
export interface PreviaNoPainel {
  saida: SaidaDaEdicao;
  linha: LinhaDoPacoteData | undefined;
  preparando: boolean;
}

export interface PainelDoDocumentoProps {
  client: AxiosInstance;
  caseId: string;
  aba: AbaDoPainel;
  onAba: (aba: AbaDoPainel) => void;
  aberto: DocumentoAberto;
  onMudar: (aberto: DocumentoAberto) => void;
  onFechar: () => void;
  previa: PreviaNoPainel;
}

const ABAS = [
  { value: 'documentos', label: tp.documentos, testId: 'painel-aba-documentos' },
  { value: 'previa', label: tp.previa, testId: 'painel-aba-previa' },
] as const;

export function PainelDoDocumento({
  client,
  caseId,
  aba,
  onAba,
  aberto,
  onMudar,
  onFechar,
  previa,
}: PainelDoDocumentoProps): JSX.Element {
  const titulo = aba === 'documentos' ? t.titulo : tp.previa;
  return (
    <aside
      aria-label={titulo}
      className="space-y-sc-3 border border-border bg-surface p-sc-4 xl:sticky xl:top-sc-4 xl:max-h-[calc(100vh-6rem)] xl:self-start xl:overflow-auto"
      data-testid="painel-documento"
    >
      <header className="flex items-start justify-between gap-sc-2">
        <Seg options={ABAS} value={aba} onChange={onAba} label={tp.abas} />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={t.fechar}
          onClick={onFechar}
          data-testid="painel-documento-fechar"
        >
          ×
        </Button>
      </header>
      <h2 className="text-body-lg font-semibold text-fg">{titulo}</h2>
      {aba === 'documentos' ? (
        <DocumentosDoPainel client={client} caseId={caseId} aberto={aberto} onMudar={onMudar} />
      ) : (
        <PreviaDaSaida
          client={client}
          caseId={caseId}
          saida={previa.saida}
          linha={previa.linha}
          preparando={previa.preparando}
          semTitulo
          avisoDePreparo={ptBR.documentoDaMesa.previaAtualizando}
        />
      )}
    </aside>
  );
}
